import { Dialog, Tab, Tabs } from "@mui/material";
import styled from "styled-components/macro";
import { alertActionButtonStyles } from "../AlertActionButton.styles";
import { themePalette } from "../../theme";

const ALERT_MUTED_TEXT_COLOR = themePalette.greyDark;
const ALERT_MUTED_ICON_COLOR = themePalette.greyLight;
const MOBILE_MAX_WIDTH = 600;
const DESKTOP_MIN_WIDTH = 1301;
const ALERTS_BADGE_COMPACT_DESKTOP_MAX_WIDTH = 1440;

export const AlertsPill = styled.div<{ $muted?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  width: fit-content;
  max-width: 100%;
  height: 45px;
  padding-inline: 16px;
  border: 2px solid
    ${({ $muted }) => ($muted ? ALERT_MUTED_TEXT_COLOR : themePalette.redDark)};
  border-radius: 22.5px;
  background: transparent;
  color: ${({ $muted }) =>
    $muted ? ALERT_MUTED_TEXT_COLOR : themePalette.redDark};
  white-space: nowrap;
  box-sizing: border-box;
`;

export const AlertsPillLabel = styled.span<{ $muted?: boolean }>`
  font-family: bold;
  font-size: 21px;
  line-height: 20px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${({ $muted }) =>
    $muted ? ALERT_MUTED_TEXT_COLOR : themePalette.redDark};
`;

export const AlertsPillSections = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
`;

export const AlertsPillSection = styled.button<{ $muted?: boolean }>`
  && {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 0;
    background: transparent;
    border: none;
    color: ${({ $muted }) =>
      $muted ? ALERT_MUTED_ICON_COLOR : themePalette.redDark};
    --alerts-pill-accent: ${({ $muted }) =>
      $muted ? ALERT_MUTED_ICON_COLOR : themePalette.redDark};
    cursor: ${({ $muted }) => ($muted ? "default" : "pointer")};
    height: auto;
    min-width: 0;
    font-size: inherit;
  }

  &:hover {
    opacity: ${({ $muted }) => ($muted ? 1 : 0.7)};
  }

  &:focus-visible {
    outline: 2px solid
      ${({ $muted }) =>
        $muted ? ALERT_MUTED_ICON_COLOR : themePalette.redDark};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

export const AlertsPillIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: inherit;
`;

export const AlertsPillBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 2px;
  border-radius: 3px;
  background: var(--alerts-pill-accent, ${themePalette.redDark});
  color: ${themePalette.white};
  font-family: light;
  font-size: 11px;
  line-height: 12px;

  @media (min-width: ${DESKTOP_MIN_WIDTH}px) and (max-width: ${ALERTS_BADGE_COMPACT_DESKTOP_MAX_WIDTH}px),
    (max-width: ${MOBILE_MAX_WIDTH}px) {
    display: none;
  }
`;

export const AlertsPillBadgeLoader = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;

  @media (min-width: ${DESKTOP_MIN_WIDTH}px) and (max-width: ${ALERTS_BADGE_COMPACT_DESKTOP_MAX_WIDTH}px),
    (max-width: ${MOBILE_MAX_WIDTH}px) {
    display: none;
  }
`;

export const AlertTabContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const AlertTabMessage = styled.div`
  font-size: 14px;
  line-height: 22px;
  color: ${themePalette.greyDark};
`;

export const CfcEligibilityDialog = styled(Dialog)`
  .MuiDialog-container {
    align-items: center;
  }

  .MuiDialog-paper {
    display: flex;
    flex-direction: column;
    max-width: 720px;
    width: 100%;
    height: 600px;
    max-height: calc(100dvh - 64px);
    margin: 32px;
    border-radius: 6px;
    overflow: hidden;
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);

    @media (max-width: 800px) {
      margin: 12px;
      max-height: calc(100dvh - 24px);
      height: calc(100dvh - 24px);
    }

    @media (max-height: 760px) {
      margin-block: 24px;
      max-height: calc(100dvh - 48px);
    }
  }
`;

export const CfcEligibilityHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 72px;
  padding: 24px;
  box-sizing: border-box;
  background-color: ${themePalette.redPastel};

  .MuiIconButton-root {
    color: ${themePalette.redMedium};
  }
`;

export const CfcEligibilityHeaderTitle = styled.h2`
  margin: 0;
  font-family: bold;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0;
  color: ${themePalette.redDark};
  text-transform: uppercase;
`;

export const CfcEligibilityTabsContainer = styled.div`
  flex-shrink: 0;
  padding: 8px 0 0 8px;
  border-bottom: 2px solid ${themePalette.neutral100};
`;

export const CfcEligibilityTabs = styled(Tabs)<{ $alertSelected?: boolean }>`
  && {
    min-height: 58px;
  }

  & .MuiTabs-scroller {
    overflow-x: auto !important;
  }

  & .MuiTabs-indicator {
    background-color: ${({ $alertSelected }) =>
      $alertSelected ? themePalette.redDark : themePalette.blueDark};
    height: 2px;
  }
`;

export const CfcEligibilityTab = styled(Tab)`
  && {
    align-items: flex-start;
    justify-content: flex-end;
    min-height: 58px;
    padding: 9px 16px;
    font-size: 14px;
    font-family: light;
    line-height: 24px;
    letter-spacing: 0;
    color: ${themePalette.greyMedium};
    text-transform: uppercase;
    white-space: nowrap;
    opacity: 1;

    &.Mui-selected {
      font-family: bold;
      color: ${themePalette.blueDark};
    }
  }
`;

export const CfcEligibilityTabLabel = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 24px;
`;

export const CfcEligibilityTabEyebrow = styled.span`
  font-family: light;
  font-size: 12px;
  line-height: 16px;
  color: ${themePalette.redMedium};
  text-transform: none;
`;

export const CfcEligibilityTabAlert = styled(CfcEligibilityTab)`
  && {
    &.Mui-selected {
      color: ${themePalette.redDark};
    }
  }
`;

export const CfcEligibilityBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  padding: 24px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

export const CfcEligibilityHeading = styled.div`
  font-size: 18px;
  font-family: bold;
  color: ${themePalette.redDark};
  line-height: 20px;
  margin-bottom: 8px;
`;

export const CfcEligibilityDescription = styled.div`
  font-size: 16px;
  font-family: light;
  color: ${themePalette.greyDark};
  line-height: 24px;
  margin-bottom: 24px;
`;

export const CfcEligibilityDescriptionBold = styled.span`
  font-family: bold;
`;

export const CfcEligibilityDescriptionAlert = styled.span`
  font-family: bold;
  color: ${themePalette.redDark};
`;

export const CfcEligibilityTable = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${themePalette.neutral200};
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 8px;
`;

const cfcEligibilityTableColumns =
  "200px minmax(220px, 345px) minmax(80px, 1fr)";

export const CfcEligibilityTableHeader = styled.div`
  display: grid;
  grid-template-columns: ${cfcEligibilityTableColumns};
  column-gap: 12px;
  background: ${themePalette.white};
  border-bottom: 1px solid ${themePalette.neutral100};
  padding: 0 8px;
  height: 36px;
  flex-shrink: 0;
  align-items: center;

  & > :last-child {
    text-align: center;
  }

  & > :nth-child(2) {
    color: ${themePalette.redDark};
  }
`;

export const CfcEligibilityTableHeaderCell = styled.div`
  font-size: 12px;
  font-family: bold;
  color: ${themePalette.greyLight};
  text-transform: uppercase;
  line-height: 18px;
  letter-spacing: 0.48px;
`;

export const CfcEligibilityTableBody = styled.div`
  max-height: 220px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

export const CfcEligibilityRow = styled.div`
  display: grid;
  grid-template-columns: ${cfcEligibilityTableColumns};
  column-gap: 12px;
  padding: 0 8px;
  height: 44px;
  align-items: center;
  background: ${themePalette.white};
  box-sizing: border-box;

  &:not(:last-child) {
    border-bottom: 1px solid ${themePalette.neutral100};
  }
`;

export const CfcEligibilityPatientName = styled.div`
  font-size: 14px;
  font-family: light;
  line-height: 20px;
  color: ${themePalette.greyMedium};
`;

export const CfcEligibilityDate = styled.div`
  font-size: 14px;
  font-family: bold;
  line-height: 20px;
  color: ${themePalette.redDark};
`;

export const CfcEligibilityActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

export const CfcEligibilityWarningRow = styled.div`
  display: grid;
  grid-template-columns: ${cfcEligibilityTableColumns};
  column-gap: 12px;
  padding: 0 8px;
  margin-bottom: 24px;

  @media (max-width: 700px) {
    display: block;
    padding: 0;
  }
`;

export const CfcEligibilityWarning = styled.div`
  grid-column: 2;
  max-width: 345px;
  font-size: 12px;
  font-family: light;
  line-height: 18px;
  color: ${themePalette.redDark};
  white-space: pre-wrap;

  @media (max-width: 700px) {
    max-width: none;
  }
`;

export const CfcEligibilityFooter = styled.div`
  flex-shrink: 0;
  margin-top: auto;
  font-size: 14px;
  font-family: light;
  color: ${themePalette.greyMedium};
  line-height: 20px;

  @media (max-width: 800px) {
    margin-top: 24px;
  }
`;

export const CfcEligibilitySupportLink = styled.a`
  color: ${themePalette.greyMedium};
  text-decoration: underline;
`;

export const CfcEligibilityActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  ${alertActionButtonStyles}

  &:disabled {
    cursor: not-allowed;
  }
`;
